import { __assign, __awaiter, __generator, __read } from "tslib";
import { ROUTES } from "@router/routes";
import React, { useCallback, useContext, useMemo, useState } from "react";
import { CompanyExtractors } from "@shared/models/company";
import { DefaultTagGroupDetail } from "../settings/DefaultTagGroupDetail";
import { ColouredTag } from "@components/common/ColouredTag";
import { CompanyAPIContext } from "@shared/contexts/api/CompanyAPIContext";
import { useSearchPagination } from "@hooks/useSearchPagination";
import { TagContextMenu } from "../settings/TagContextMenu";
import { useNavigation } from "@hooks/useNavigation";
import cn from "classnames";
import { useAppToast } from "../common/Toast";
import { MergeTagDialog, useMergeTagDialog } from "../common/MergeTagDialog";
import { TagGroupSlug } from "@shared/models/tag";
export var CompanyTagGroupDetailScreen = React.memo(function () {
    var navigation = useNavigation();
    var companyAPI = useContext(CompanyAPIContext);
    var _a = useAppToast(), showErrorToast = _a.showErrorToast, showSuccessToast = _a.showSuccessToast;
    var _b = __read(useState({}), 2), enableStates = _b[0], setEnableStates = _b[1];
    var onGoBack = useCallback(function () {
        if (!navigation.goBack()) {
            navigation.push(ROUTES.settings.tags.root);
        }
    }, [navigation]);
    var onFetchListItemError = useCallback(function () {
        showErrorToast({
            titleId: "common.errors.data.fetch_failed.title",
            descriptionId: "common.errors.data.fetch_failed.description",
        }, {
            position: "bottom-center",
        });
        onGoBack();
    }, [onGoBack, showErrorToast]);
    var _c = useSearchPagination(companyAPI.listCompanies, {
        page: 1,
        perPage: 100,
        isEnabled: "any",
    }, onFetchListItemError), data = _c.data, totalCount = _c.totalCount, isInitializing = _c.isInitializing, isFetchingMore = _c.isFetchingMore, hasMore = _c.hasMore, nextPage = _c.nextPage, search = _c.search, refresh = _c.refresh;
    var onAddNewCompany = useCallback(function () {
        navigation.push(ROUTES.settings.tags.company.add);
    }, [navigation]);
    var onEditGroup = useCallback(function () {
        navigation.push(ROUTES.settings.tags.company.edit_group);
    }, [navigation]);
    var onEditTag = useCallback(function (company) {
        navigation.push("".concat(ROUTES.settings.tags.company.group_detail, "/").concat(company.id, "/edit"), {
            tagName: company.name,
        });
    }, [navigation]);
    var onEnableTag = useCallback(function (company) { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            setEnableStates(function (prevState) {
                var _a;
                return (__assign(__assign({}, prevState), (_a = {}, _a[company.id] = true, _a)));
            });
            companyAPI
                .enableCompany(company.id)
                .then(function () {
                showSuccessToast({
                    titleId: "settings.tags.enable_tag.success.title",
                }, {
                    position: "bottom-center",
                });
            })
                .catch(function (err) {
                console.error("Failed to enable tag", err);
                showErrorToast({
                    titleId: "settings.tags.enable_tag.error.title",
                    descriptionId: "settings.tags.enable_tag.error.description",
                }, {
                    position: "bottom-center",
                });
                setEnableStates(function (prevState) {
                    var _a;
                    return (__assign(__assign({}, prevState), (_a = {}, _a[company.id] = company.isEnabled, _a)));
                });
            });
            return [2 /*return*/];
        });
    }); }, [companyAPI, showErrorToast, showSuccessToast]);
    var onDisableTag = useCallback(function (company) { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            setEnableStates(function (prevState) {
                var _a;
                return (__assign(__assign({}, prevState), (_a = {}, _a[company.id] = false, _a)));
            });
            companyAPI
                .disableCompany(company.id)
                .then(function () {
                showSuccessToast({
                    titleId: "settings.tags.disable_tag.success.title",
                }, {
                    position: "bottom-center",
                });
            })
                .catch(function (err) {
                console.error("Failed to disable tag", err);
                showErrorToast({
                    titleId: "settings.tags.disable_tag.error.title",
                    descriptionId: "settings.tags.disable_tag.error.description",
                }, {
                    position: "bottom-center",
                });
                setEnableStates(function (prevState) {
                    var _a;
                    return (__assign(__assign({}, prevState), (_a = {}, _a[company.id] = company.isEnabled, _a)));
                });
            });
            return [2 /*return*/];
        });
    }); }, [companyAPI, showErrorToast, showSuccessToast]);
    var onDeleteTag = useCallback(function (company) { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            return [2 /*return*/, companyAPI.deleteCompany(company.id).then(function () {
                    refresh();
                })];
        });
    }); }, [companyAPI, refresh]);
    var _d = useMergeTagDialog(), mergeDialogProps = _d.mergeDialogProps, openMergeDialog = _d.openMergeDialog, closeMergeDialog = _d.closeMergeDialog;
    var _e = __read(useState(false), 2), isMerging = _e[0], setIsMerging = _e[1];
    var _f = __read(useState(null), 2), mergeItem = _f[0], setMergeItem = _f[1];
    var onMergeTag = useCallback(function (item) { return __awaiter(void 0, void 0, void 0, function () {
        var source, err_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    setMergeItem(item);
                    return [4 /*yield*/, openMergeDialog()];
                case 1:
                    source = _a.sent();
                    if (!source) {
                        return [2 /*return*/];
                    }
                    setIsMerging(true);
                    _a.label = 2;
                case 2:
                    _a.trys.push([2, 4, 5, 6]);
                    return [4 /*yield*/, companyAPI.mergeCompany(item.id, source.id)];
                case 3:
                    _a.sent();
                    closeMergeDialog();
                    refresh();
                    return [3 /*break*/, 6];
                case 4:
                    err_1 = _a.sent();
                    console.error("Failed to merge tag", err_1);
                    showErrorToast({
                        titleId: "merge_tag.error.title",
                        descriptionId: "merge_tag.error.description",
                    }, {
                        position: "bottom-center",
                    });
                    return [3 /*break*/, 6];
                case 5:
                    setIsMerging(false);
                    return [7 /*endfinally*/];
                case 6: return [2 /*return*/];
            }
        });
    }); }, [openMergeDialog, companyAPI, closeMergeDialog, refresh, showErrorToast]);
    var onGoToTagDetail = useCallback(function (company) {
        navigation.push("".concat(ROUTES.settings.tags.company.group_detail, "/").concat(company.id), {
            tagName: company.name,
        });
    }, [navigation]);
    var renderContextMenu = useCallback(function (c) {
        var _a;
        return (React.createElement(TagContextMenu, { typeId: "settings.tags.title.company", item: c, itemEnableState: (_a = enableStates[c.id]) !== null && _a !== void 0 ? _a : c.isEnabled, onEdit: onEditTag, onEnable: onEnableTag, onDisable: onDisableTag, onDelete: onDeleteTag, onMerge: onMergeTag }));
    }, [
        enableStates,
        onEditTag,
        onEnableTag,
        onDisableTag,
        onDeleteTag,
        onMergeTag,
    ]);
    var tableColumns = useMemo(function () { return [
        {
            titleId: "settings.tags.company.detail.table.company_tag",
            accessor: function (c) { return c.name; },
            render: function (_, c) {
                var _a;
                return (React.createElement(ColouredTag, { className: cn(!((_a = enableStates[c.id]) !== null && _a !== void 0 ? _a : c.isEnabled) && "opacity-50"), tag: c.name, colour: c.color }));
            },
        },
        {
            titleId: "settings.tags.company.detail.table.full_name",
            accessor: function (c) { return c.fullName; },
        },
        {
            titleId: "settings.tags.company.detail.table.country",
            accessor: function (c) { return c.countries.map(function (country) { return country.name; }).join(", "); },
        },
    ]; }, [enableStates]);
    var tableActions = useMemo(function () { return [
        {
            className: "flex items-center justify-end",
            render: renderContextMenu,
        },
    ]; }, [renderContextMenu]);
    return (React.createElement(React.Fragment, null,
        React.createElement(DefaultTagGroupDetail, { headerTitleId: "settings.tags.title.company", headerTagCountId: "settings.tags.tag_counts.company", searchPlaceholderId: "settings.tags.company.detail.search_placeholder", addNewTagId: "settings.tags.company.detail.add_new_company", tableData: data, tableColumns: tableColumns, tableActions: tableActions, tagCount: totalCount, isInitializing: isInitializing, isFetchingMore: isFetchingMore, hasMoreData: hasMore, onFetchMore: nextPage, onGoBack: onGoBack, onAddNewClick: onAddNewCompany, onEditGroup: onEditGroup, onTableItemClick: onGoToTagDetail, onSearch: search, tagGroupSlug: TagGroupSlug.Company }),
        mergeItem && (React.createElement(MergeTagDialog, __assign({}, mergeDialogProps, { destinationId: mergeItem.id, tagName: mergeItem.name, isMerging: isMerging, fetch: companyAPI.listCompanies, keyExtractor: CompanyExtractors.id, labelExtractor: CompanyExtractors.name })))));
});
